
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import OtpInput from "@/components/OtpInput";
import { CheckCircle, Loader2 } from "lucide-react";

interface VerificationStepProps {
  email: string;
  resendDisabled: boolean;
  countdown: number;
  onOtpComplete: (otp: string) => void;
  onResendOtp: () => void;
  onBack: () => void;
  autoFillOtp: boolean;
  verifyingPhone?: boolean;
  phoneNumber?: string;
}

const VerificationStep: React.FC<VerificationStepProps> = ({ 
  email, 
  resendDisabled, 
  countdown, 
  onOtpComplete, 
  onResendOtp, 
  onBack,
  autoFillOtp,
  verifyingPhone,
  phoneNumber
}) => {
  const [currentOtp, setCurrentOtp] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  
  // Handler for validation button
  const handleValidate = async () => {
    if (currentOtp.length !== 6) {
      console.log("Validation attempted with incomplete OTP:", currentOtp);
      return;
    }
    
    console.log("Starting OTP validation for code:", currentOtp);
    setIsValidating(true);
    
    try {
      await onOtpComplete(currentOtp);
    } finally {
      setIsValidating(false);
    }
  };

  // Auto-submit when OTP is complete (optional)
  useEffect(() => {
    if (currentOtp.length === 6 && !isValidating) {
      console.log("OTP complete, auto-submitting:", currentOtp);
      handleValidate();
    }
  }, [currentOtp, isValidating]);

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle className="text-center">EasyDJ+ Vérification</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="bg-indigo-50 p-3 mb-4 rounded-md border border-indigo-200">
          <p className="text-center text-indigo-800">
            {verifyingPhone 
              ? "Nous avons envoyé un code par SMS au numéro:"
              : "Nous avons envoyé un code à l'adresse:"}
          </p>
          <p className="text-center font-semibold text-indigo-900 break-all">
            {verifyingPhone ? phoneNumber : email}
          </p>
        </div>
        
        <OtpInput
          length={6}
          onOtpChange={setCurrentOtp}
          onResendOtp={onResendOtp}
          isResendDisabled={resendDisabled}
          resendCountdown={countdown}
          autoFill={autoFillOtp}
        />
        
        <div className="mt-6 flex justify-center">
          <Button 
            onClick={handleValidate}
            disabled={currentOtp.length !== 6 || isValidating}
            className="w-full"
            variant="indigo"
          >
            {isValidating ? (
              <>
                <Loader2 className="mr-2 animate-spin" size={18} />
                Vérification...
              </>
            ) : (
              <>
                <CheckCircle className="mr-2" size={18} />
                Valider le code
              </>
            )}
          </Button>
        </div>
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button 
          variant="outline" 
          onClick={onBack}
          disabled={isValidating}
          className="text-sm"
        >
          Retour
        </Button>
        
        <Button 
          variant="ghost" 
          onClick={onResendOtp}
          disabled={resendDisabled || isValidating}
          className="text-sm"
        >
          {resendDisabled 
            ? `Renvoyer (${countdown}s)` 
            : "Renvoyer le code"}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default VerificationStep;
